import type { AccountFacilitiesFetchedAction } from '@Application/Facilities/Facilities/Actions/accountFacilitiesFetched';
import type { AccountFacilitiesFetchFailedAction } from '@Application/Facilities/Facilities/Actions/accountFacilitiesFetchFailed';
import type { FacilityCreatedAction } from '@Application/Facilities/Facilities/Actions/facilityCreated';
import type { FacilityDeletedAction } from '@Application/Facilities/Facilities/Actions/facilityDeleted';
import type { FacilityUpdatedAction } from '@Application/Facilities/Facilities/Actions/facilityUpdated';
import type { OrganizationDeletedAction } from '@Application/Organizations/Organizations/Actions/organizationDeleted';
import type { UserRevokedAction } from '@Application/Security/Authentications/Actions/userRevoked';
import type { ExtractedMessages } from '@Infra/FetchLib/ApiError/extractMessages';
import AccountFacilitiesListView from '@UI/Store/AccountFacilities/Views/AccountFacilitiesListView';
import AccountFacilityView from '@UI/Store/AccountFacilities/Views/AccountFacilityView';
import { createSliceReducer, registerStore } from '@verclam/synapse';

export type AccountFacilitiesStore = {
	accountFacilities: AccountFacilitiesListView | null;
	error: ExtractedMessages;
};

const initialState: AccountFacilitiesStore = {
	accountFacilities: null,
	error: undefined,
};

type Actions =
	| AccountFacilitiesFetchedAction
	| AccountFacilitiesFetchFailedAction
	| FacilityCreatedAction
	| FacilityUpdatedAction
	| FacilityDeletedAction
	| OrganizationDeletedAction
	| UserRevokedAction;

const whenAccountFacilitiesFetched = (
	store: AccountFacilitiesStore,
	action: AccountFacilitiesFetchedAction,
): AccountFacilitiesStore => ({
	...store,
	accountFacilities: new AccountFacilitiesListView(
		action.payload.map((facility) => new AccountFacilityView(facility)),
		action.accountId,
	),
	error: undefined,
});

// The list itself is kept on failure (no data loss); the error is kept so the screen can explain why the
// startup gate stayed empty (e.g. a 403 on `FACILITY_READ`) instead of a blank state. A pending refresh
// request is dropped so a failing re-read is never retried in a loop.
const whenAccountFacilitiesFetchFailed = (
	store: AccountFacilitiesStore,
	action: AccountFacilitiesFetchFailedAction,
): AccountFacilitiesStore => ({
	...store,
	accountFacilities: store.accountFacilities?.clearRefreshRequest() ?? null,
	error: action.payload,
});

// The endpoint returns only the facilities assigned to the caller, which the client cannot deduce from the
// mutation: the list is flagged for a server re-read and stays displayed meanwhile.
const whenFacilitiesMayHaveChanged = (store: AccountFacilitiesStore): AccountFacilitiesStore => {
	if (store.accountFacilities === null) {
		return store;
	}

	return { ...store, accountFacilities: store.accountFacilities.requestRefresh() };
};

const whenUserRevoked = (): AccountFacilitiesStore => initialState;

export default registerStore<'accountFacilitiesStore', Actions>(
	'accountFacilitiesStore',
	createSliceReducer({
		ACCOUNT_FACILITIES_FETCHED: whenAccountFacilitiesFetched,
		ACCOUNT_FACILITIES_FETCH_FAILED: whenAccountFacilitiesFetchFailed,
		FACILITY_CREATED: whenFacilitiesMayHaveChanged,
		FACILITY_UPDATED: whenFacilitiesMayHaveChanged,
		FACILITY_DELETED: whenFacilitiesMayHaveChanged,
		ORGANIZATION_DELETED: whenFacilitiesMayHaveChanged,
		USER_REVOKED: whenUserRevoked,
	}),
	initialState,
);
